// support.js — Logique de la popup « soutiens VertinGo » (abonnement chaînes + likes réseaux).
// Cible selon l'ACTIVITÉ (ex: bcp de vidéos Saga regardées + pas abonné → propose Saga), ne la
// remontre pas pour une source déjà engagée, et respecte un bon timing (user récurrent + cooldown).
import { nakamaAuth } from './nakamaAuth';

const ACT_KEY = 'vg_support_act';       // compteurs d'activité (localStorage)
const ENG_KEY = 'vg_support_engaged';   // sources déjà engagées (abonné/liké)
const COLL = 'vg_social';               // sync cloud Nakama (cross-device)

// Gating « bon timing » : user récurrent + pas trop souvent.
const MIN_OPENS = 3;             // au moins 3 ouvertures de l'app
const MIN_TOTAL_VIEWS = 3;       // …ou 3 vidéos regardées
const CHAN_AFFINITY = 2;         // ≥2 vidéos d'une chaîne = affinité → on cible celle-là
// BACKOFF ESCALADANT : à chaque fois que la popup est MONTRÉE sans engagement, on attend de plus en plus longtemps
// (jours). Après MAX_PROMPTS montrées-ignorées, on arrête complètement (on ne harcèle pas). Un « Plus tard » explicite
// compte comme un affichage de plus (recule encore). S'abonner remet le compteur à zéro (signal positif → on pourra
// reproposer UNE AUTRE chaîne plus tard, après un délai). Priorité d'ordre : ordre de PRIORITÉ des chaînes.
const BACKOFF_DAYS = [3, 8, 21];  // 1re fois ignorée → 3 j, 2e → 8 j, 3e → 21 j
const MAX_PROMPTS = 4;            // au-delà : on abandonne (plus jamais tant que rien ne change)
const CHAN_PRIORITY = ['saga', 'horizons', 'vertingo'];

function _load(key, def) {
  try { const v = JSON.parse(localStorage.getItem(key)); return v == null ? def : v; } catch { return def; }
}
function _save(key, val) { try { localStorage.setItem(key, JSON.stringify(val)); } catch { /* */ } }

function getAct() {
  const a = _load(ACT_KEY, null) || { opens: 0, views: {}, firstSeen: Date.now(), lastPromptTs: 0, shownCount: 0, lastId: '' };
  if (!a.views) a.views = {};
  if (a.shownCount == null) a.shownCount = 0;
  return a;
}
function setAct(a) { _save(ACT_KEY, a); }

export function recordAppOpen() {
  const a = getAct(); a.opens = (a.opens || 0) + 1; setAct(a);
}
export function recordVideoView(channelKey) {
  if (!channelKey) return;
  const a = getAct(); a.views[channelKey] = (a.views[channelKey] || 0) + 1; setAct(a);
}
function totalViews(a) { return Object.values(a.views || {}).reduce((s, n) => s + (n || 0), 0); }

// ── Sources engagées (abonné/liké) : localStorage + sync Nakama best-effort ──
let _engaged = new Set(_load(ENG_KEY, []));
export function isEngaged(id) { return _engaged.has(id); }
export function markEngaged(id) {
  _engaged.add(id);
  _save(ENG_KEY, [...(_engaged)]);
  // Signal POSITIF : l'user a cliqué pour s'abonner → on remet le compteur d'affichages à 0 et on pose un délai
  // modéré, pour pouvoir reproposer UNE AUTRE chaîne plus tard (ex. Horizons après Saga) sans le brusquer.
  const a = getAct(); a.shownCount = 0; a.lastPromptTs = Date.now(); a.lastId = id; setAct(a);
  try { if (nakamaAuth.session && !nakamaAuth.session._localGuest) nakamaAuth.storageWrite(COLL, 'engaged', [..._engaged]); } catch { /* */ }
}
// Fusionne les sources engagées stockées dans le cloud (au login) → cross-device.
export async function syncEngaged() {
  try {
    if (!nakamaAuth.session || nakamaAuth.session._localGuest) return;
    const r = await nakamaAuth.storageRead(COLL, 'engaged');
    if (Array.isArray(r)) { r.forEach((x) => _engaged.add(x)); _save(ENG_KEY, [..._engaged]); }
  } catch { /* */ }
}

// La popup a été MONTRÉE (impression) : on pose le cooldown TOUT DE SUITE (même si l'user l'ignore et ne clique
// rien) → fini « elle revient à chaque session ». Chaque affichage recule le prochain (backoff escaladant).
export function recordShown(id) {
  const a = getAct();
  a.shownCount = (a.shownCount || 0) + 1;
  a.lastPromptTs = Date.now();
  a.lastId = id || a.lastId;
  setAct(a);
}
// « Plus tard » = refus explicite → compte comme un affichage de plus (recule encore le prochain rappel).
export function snooze() { const a = getAct(); a.shownCount = (a.shownCount || 0) + 1; a.lastPromptTs = Date.now(); setAct(a); }

// Choisit UNE source à proposer (ou null). `available` = ids dont l'URL existe réellement.
//  1) BACKOFF : au-delà de MAX_PROMPTS affichages ignorés → null (on arrête). Sinon on respecte le délai escaladant.
//  2) CIBLAGE : chaîne avec le PLUS d'affinité (vidéos vues) non abonnée ; sinon 1re chaîne non abonnée par priorité
//     (→ propose naturellement Horizons quand Saga est déjà pris) ; sinon un réseau non liké. Jamais 2× la même de suite.
export function pickProposal(available) {
  const a = getAct();
  const recurrent = (a.opens || 0) >= MIN_OPENS || totalViews(a) >= MIN_TOTAL_VIEWS;
  if (!recurrent) return null;
  if ((a.shownCount || 0) >= MAX_PROMPTS) return null;                          // trop montrée-ignorée → on n'insiste plus
  const wait = BACKOFF_DAYS[Math.min(a.shownCount || 0, BACKOFF_DAYS.length - 1)] * 864e5;
  if (a.lastPromptTs && Date.now() - a.lastPromptTs < wait) return null;        // délai escaladant pas écoulé

  const notSame = (id) => id !== a.lastId;                                      // évite de reproposer la même source d'affilée
  const eligible = (id) => !isEngaged(id) && available.includes(id);

  // (2a) affinité forte : la chaîne la plus regardée (≥ seuil), non abonnée
  const byViews = CHAN_PRIORITY.slice().sort((x, y) => (a.views[y] || 0) - (a.views[x] || 0));
  for (const ch of byViews) {
    const id = 'yt_' + ch;
    if ((a.views[ch] || 0) >= CHAN_AFFINITY && eligible(id) && notSame(id)) return id;
  }
  // (2b) rotation par priorité : 1re chaîne non abonnée (Saga → Horizons → VertinGo), en évitant la dernière montrée
  for (const ch of CHAN_PRIORITY) { const id = 'yt_' + ch; if (eligible(id) && notSame(id)) return id; }
  for (const ch of CHAN_PRIORITY) { const id = 'yt_' + ch; if (eligible(id)) return id; }   // repli : accepte la même si c'est la seule
  // (2c) réseaux sociaux non likés
  for (const id of ['facebook', 'tiktok']) if (eligible(id) && notSame(id)) return id;
  for (const id of ['facebook', 'tiktok']) if (eligible(id)) return id;
  return null;
}
